import { observable, reaction } from "mobx";

const BOTH = "BOTH";
const TOP = "TOP";
const BOTTOM = "BOTTOM";
const DOWN = "DOWN";
const UP = "UP";
const CENTER = "CENTER";

export class TrackGadget {
  @observable notifyForegroundShouldRedraw = 0;
  @observable notifyBackgroundShouldRedraw = 0;
  originY = 0;
  height = 0;
  midpointY = 0;
  bottomY = 0;
  doesRollover = false;
  detectPixelForRollover = false;

  timePixelScale;
  canvasPixelWidth;
  canvasStartTime;
  canvasTimeExtent;
  canvasEndTime;

  disposers = [];

  static BOTH = BOTH;
  static TOP = TOP;
  static BOTTOM = BOTTOM;
  static DOWN = DOWN;
  static UP = UP;
  static CENTER = CENTER;

  yInside(y) {
    return y > this.originY && y < this.bottomY;
  }

  setLayout(originY, height) {
    this.originY = originY;
    this.height = height;
    this.midpointY = this.originY + this.height / 2;
    this.bottomY = this.originY + height;
  }

  setCanvasMapping(
    canvasStartTime,
    canvasTimeExtent,
    canvasPixelWidth,
    timePixelScale
  ) {
    this.canvasStartTime = canvasStartTime;
    this.canvasTimeExtent = canvasTimeExtent;
    this.canvasPixelWidth = canvasPixelWidth;
    this.timePixelScale = timePixelScale;
    this.requestFullRedraw();
  }

  drawBackgroundLayer(ctx) {
    throw new Error("non implemented abstract");
  }

  drawForegroundLayer(ctx) {
    throw new Error("non implemented abstract");
  }

  draw(ctx) {
    this.drawBackgroundLayer(ctx);
    this.drawForegroundLayer(ctx);
  }

  onBackgroundShouldRedraw(f) {
    this.disposers.push(reaction(() => this.notifyBackgroundShouldRedraw, f));
  }

  onForegroundShouldRedraw(f) {
    this.disposers.push(reaction(() => this.notifyForegroundShouldRedraw, f));
  }

  requestBackgroundRedraw() {
    this.notifyBackgroundShouldRedraw++;
  }

  requestForegroundRedraw() {
    this.notifyForegroundShouldRedraw++;
  }

  requestFullRedraw() {
    this.requestForegroundRedraw();
    this.requestBackgroundRedraw();
  }

  handleMouseClickAtTime(x, y, time, event) {}

  timeExtentToPixelCount(extent) {
    return extent * this.timePixelScale;
  }

  timeToCanvasPixelX(time) {
    return (time - this.canvasStartTime) * this.timePixelScale;
  }

  timeIsOnCanvas(time) {
    return time > this.canvasStartTime && time < this.canvasEndTime;
  }

  timeIntervalIsOnCanvas(start, end) {
    return !(end < this.canvasStartTime || start > this.canvasEndTime);
  }

  drawTimerangeEdges(ctx, timeInterval, pixelHeight) {
    ctx.beginPath();
    const x1 = this.timeToCanvasPixelX(timeInterval.start);
    const x2 = this.timeToCanvasPixelX(timeInterval.end);
    ctx.moveTo(x1, this.bottomY);
    ctx.lineTo(x1, this.bottomY - pixelHeight);
    ctx.moveTo(x2, this.bottomY);
    ctx.lineTo(x2, this.bottomY - pixelHeight);
    ctx.stroke();
  }

  drawTimerangeRect(ctx, timeInterval, pixelHeight) {
    ctx.beginPath();
    ctx.rect(
      this.timeToCanvasPixelX(timeInterval.start),
      this.bottomY - pixelHeight,
      this.timeExtentToPixelCount(timeInterval.end - timeInterval.start),
      pixelHeight
    );
    ctx.fill();
  }

  // TODO factor out with above
  drawTimerangeRectOutline(ctx, timeInterval, pixelHeight) {
    ctx.beginPath();
    ctx.rect(
      this.timeToCanvasPixelX(timeInterval.start),
      this.bottomY - pixelHeight,
      this.timeExtentToPixelCount(timeInterval.end - timeInterval.start),
      pixelHeight
    );
    ctx.stroke();
  }

  drawTimerangeDividingLine(ctx, timeInterval, ratio) {
    const y = this.originY + this.height * ratio;
    ctx.beginPath();
    ctx.moveTo(this.timeToCanvasPixelX(timeInterval.start), y);
    ctx.lineTo(this.timeToCanvasPixelX(timeInterval.end), y);
    ctx.stroke();
  }

  drawTextAtTime(ctx, time, text, leadingSpace, width = -1) {
    const x = this.timeToCanvasPixelX(time) + leadingSpace;
    if (width >= 0) {
      ctx.fillText(text, x, this.midpointY, width - leadingSpace);
    } else {
      ctx.fillText(text, x, this.midpointY);
    }
  }

  drawTextInTimeInterval(ctx, interval, text, alignment = CENTER) {
    const width = this.timeExtentToPixelCount(interval.end - interval.start);
    let x;
    ctx.save();
    if (alignment === CENTER) {
      ctx.textAlign = "center";
      x = this.timeToCanvasPixelX((interval.start + interval.end) / 2);
    } else {
      x = this.timeToCanvasPixelX(interval.start) + 5; //TODO calc spacer value from DPI?
    }
    ctx.fillText(text, x, this.midpointY, width);
    ctx.restore();
  }

  drawLineAtTime(ctx, time, pixelHeight, direction = "UP") {
    const pixelX = this.timeToCanvasPixelX(time);
    if (direction === "UP") {
      ctx.moveTo(pixelX, this.bottomY);
      ctx.lineTo(pixelX, this.bottomY - pixelHeight);
    } else {
      ctx.moveTo(pixelX, this.originY);
      ctx.lineTo(pixelX, this.originY + pixelHeight);
    }
    ctx.stroke();
  }

  drawTriangleAtTime(ctx, time, pixelWidth, direction = DOWN) {
    const halfWidth = pixelWidth >> 1;
    const pixelCenter = this.timeToCanvasPixelX(time);
    if (direction === DOWN) {
      ctx.beginPath();
      ctx.moveTo(pixelCenter - halfWidth, this.originY);
      ctx.lineTo(pixelCenter, this.bottomY);
      ctx.lineTo(pixelCenter + halfWidth, this.originY);
      ctx.closePath();
      ctx.fill();
    } else {
      // TODO
    }
  }

  drawGadgetSliceBounds(ctx, pick = BOTH) {
    if (pick === BOTH || pick === TOP) {
      ctx.moveTo(0, this.originY);
      ctx.lineTo(this.canvasPixelWidth, this.originY);
    }
    if (pick === BOTH || pick === BOTTOM) {
      ctx.moveTo(0, this.bottomY);
      ctx.lineTo(this.canvasPixelWidth, this.bottomY);
    }
    ctx.stroke();
  }

  dispose() {
    for (const disposer of this.disposers) {
      disposer();
    }
    this.disposers = [];
  }
}
